import {
  ChangeEventHandler,
  FormEventHandler,
  KeyboardEventHandler,
  useRef,
  useState,
  KeyboardEvent,
  useEffect,
  useMemo
} from "react";
import classNames from "classnames";
import { VuiIconButton } from "../button/IconButton";
import { BiSearch, BiX } from "react-icons/bi";
import { VuiIcon } from "../icon/Icon";
import { VuiSearchInputSuggestions } from "./SearchInputSuggestions";
import { SearchSuggestion } from "./types";
import { createId } from "../../utils/createId";
import { VuiSpinner } from "../spinner/Spinner";

const SIZE = ["s", "m", "l"] as const;

const sizeToIconSizeMap = {
  s: "xs",
  m: "s",
  l: "m"
} as const;

type Props = {
  className?: string;
  value?: string;
  size?: (typeof SIZE)[number];
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  placeholder?: string;
  autoFocus?: boolean;
  onSubmit?: FormEventHandler;
  suggestions?: SearchSuggestion[];
  onSelectSuggestion?: (suggestion: SearchSuggestion) => void;
  isLoading?: boolean;
};

type ClearableProps =
  | {
      isClearable?: true;
      onClear: () => void;
    }
  | {
      isClearable?: false;
      onClear?: never;
    };

export const VuiSearchInput = ({
  className,
  size = "m",
  value,
  onChange,
  onKeyDown,
  placeholder,
  autoFocus,
  onSubmit,
  isClearable,
  onClear,
  suggestions,
  onSelectSuggestion,
  isLoading,
  ...rest
}: Props & ClearableProps) => {
  const classes = classNames("vuiSearchInput", `vuiSearchInput--${size}`, className);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [areSuggestionsVisible, setAreSuggestionsVisible] = useState(true);
  const suggestionRefs = useRef<(HTMLElement | null)[]>([]);
  const suppressNextFocus = useRef(false);

  // Reset suggestions visibility when suggestions change.
  const prevSuggestionsRef = useRef(suggestions);
  if (prevSuggestionsRef.current !== suggestions) {
    prevSuggestionsRef.current = suggestions;
    if (suggestions && suggestions.length > 0) {
      setAreSuggestionsVisible(true);
    }
  }

  const hasSuggestions = suggestions && suggestions.length > 0 && areSuggestionsVisible;
  const controlsId = useMemo(() => `searchSuggestions-${createId()}`, []);

  // Derive ghost text from the first suggestion if it extends the current value.
  const ghostText = useMemo(() => {
    if (!hasSuggestions || !value || !suggestions[0]?.value) return "";
    const firstValue = suggestions[0].value;
    if (firstValue.startsWith(value) && firstValue !== value) {
      return firstValue.slice(value.length);
    }
    return "";
  }, [hasSuggestions, value, suggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setAreSuggestionsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputFocus = () => {
    // Don't show suggestions if focus was triggered by Escape key
    if (suppressNextFocus.current) {
      suppressNextFocus.current = false;
      return;
    }

    // Show suggestions when input receives focus (if suggestions exist).
    if (suggestions && suggestions.length > 0) {
      setAreSuggestionsVisible(true);
    }
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();

        // Show suggestions if hidden, or move to first suggestion.
        if (suggestions && suggestions.length > 0) {
          if (!areSuggestionsVisible) {
            setAreSuggestionsVisible(true);
          } else {
            suggestionRefs.current[0]?.focus();
          }
        }
        break;
      }

      case "Escape": {
        e.preventDefault();
        setAreSuggestionsVisible(false);
        suppressNextFocus.current = true;
        inputRef.current?.focus();
        break;
      }

      case "Tab": {
        // If there's a matching value suggestion, select it instead of default tab.
        if (hasSuggestions && ghostText && onSelectSuggestion && suggestions[0]?.value) {
          e.preventDefault();
          onSelectSuggestion(suggestions[0]);
        } else {
          setAreSuggestionsVisible(false);
        }
        break;
      }

      case "Enter": {
        // Prevent form submission from triggering a page refresh.
        if (!onSubmit) e.preventDefault();
        break;
      }
    }
  };

  const handleSuggestionKeyDown = (e: KeyboardEvent<HTMLElement>, index: number) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        // Move to next suggestion, or wrap to first.
        const nextIndex = index + 1;
        if (nextIndex < suggestionRefs.current.length) {
          suggestionRefs.current[nextIndex]?.focus();
        } else {
          suggestionRefs.current[0]?.focus();
        }
        break;
      }

      case "ArrowUp": {
        e.preventDefault();
        if (index === 0) {
          // Move back to input.
          inputRef.current?.focus();
        } else {
          // Move to previous suggestion.
          suggestionRefs.current[index - 1]?.focus();
        }
        break;
      }

      case "Escape": {
        e.preventDefault();
        setAreSuggestionsVisible(false);
        suppressNextFocus.current = true;
        inputRef.current?.focus();
        break;
      }

      case "Enter": {
        // For value suggestions, trigger selection, hide suggestions, and refocus input.
        if (suggestions && suggestions[index]?.value && onSelectSuggestion) {
          e.preventDefault();
          onSelectSuggestion(suggestions[index]);
          setAreSuggestionsVisible(false);
          inputRef.current?.focus();
        }
        break;
      }

      case "Tab": {
        // Hide suggestions and allow default tab behavior.
        setAreSuggestionsVisible(false);
        break;
      }
    }
  };

  const inputClasses = classNames("vuiSearchInput__input", {
    "vuiSearchInput__input--hasSuggestions": hasSuggestions
  });

  return (
    <form onSubmit={onSubmit} role="search">
      <div
        ref={containerRef}
        className={classes}
        aria-live="polite"
        aria-atomic="true"
        aria-busy={isLoading ? "true" : "false"}
      >
        <input
          ref={inputRef}
          className={inputClasses}
          type="text"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck="false"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={handleInputFocus}
          onKeyDown={(e) => {
            handleInputKeyDown(e);
            onKeyDown?.(e);
          }}
          aria-autocomplete="list"
          aria-controls={hasSuggestions ? controlsId : undefined}
          {...rest}
        />

        {ghostText && (
          <div className="vuiSearchInput__ghostText" aria-hidden="true">
            <span className="vuiSearchInput__ghostText--hidden">{value}</span>
            <span className="vuiSearchInput__ghostText--visible">{ghostText}</span>
          </div>
        )}

        <div className="vuiSearchInput__icon">
          {isLoading ? (
            <VuiSpinner size={size === "m" ? "s" : "m"} />
          ) : (
            <VuiIcon color="subdued" size={sizeToIconSizeMap[size]}>
              <BiSearch />
            </VuiIcon>
          )}
        </div>

        {isClearable && value && (
          <VuiIconButton
            aria-label="Clear input"
            className="vuiSearchInput__clearButton"
            icon={
              <VuiIcon>
                <BiX />
              </VuiIcon>
            }
            onClick={(e) => {
              e.preventDefault();
              onClear();
            }}
          />
        )}

        {hasSuggestions && (
          <VuiSearchInputSuggestions
            id={controlsId}
            suggestions={suggestions}
            onSuggestionKeyDown={handleSuggestionKeyDown}
            suggestionRefs={suggestionRefs}
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
      </div>
    </form>
  );
};
